/**
 * SearchBox Component
 * Input with debounce, voice button, and autocomplete from history
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import clsx from 'clsx';
import type { SearchBoxProps, WidgetConfig } from '../types';
import { debounce } from '../utils/formatters';
import { getWordPressSettings } from '../utils/settings';
import './SearchBox.css';

export interface SearchBoxPropsWithConfig extends SearchBoxProps {
  config?: WidgetConfig;
}

export const SearchBox: React.FC<SearchBoxPropsWithConfig> = ({
  placeholder = 'Escribe tu consulta...',
  autoFocus = false,
  onSearch,
  onVoiceClick,
  showVoiceButton = true,
  debounceMs = 300,
  config,
}) => {
  const [value, setValue] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Get voice configuration from WordPress settings
  const wpSettings = getWordPressSettings();
  const voiceButtonPosition = wpSettings.voiceButtonPosition || 'right';
  const voiceEnabled = wpSettings.voiceEnabled !== false; // true by default

  // Load history from localStorage
  useEffect(() => {
    try {
      const stored = localStorage.getItem('ai360-search-history');
      if (stored) {
        setHistory(JSON.parse(stored));
      }
    } catch {
      // Ignore
    }
  }, []);

  // Apply input styles based on config
  useEffect(() => {
    if (!wrapperRef.current || !config) return;

    const wrapper = wrapperRef.current;
    
    // Read configurations from WordPress
    const wpSettings = getWordPressSettings();
    const inputStyle = config.inputStyle || wpSettings.inputStyle || 'default';
    const showInputBorder = config.showInputBorder ?? wpSettings.showInputBorder ?? true;
    const inputBorderColor = config.inputBorderColor || wpSettings.inputBorderColor || '#dfe1e5';
    const inputBorderWidth = config.inputBorderWidth ?? wpSettings.inputBorderWidth ?? 1;
    const inputBorderRadius = config.inputBorderRadius ?? wpSettings.inputBorderRadius ?? 24;
    const inputBackgroundColor = config.inputBackgroundColor || wpSettings.inputBackgroundColor || '#ffffff';
    const inputShadow = config.inputShadow ?? wpSettings.inputShadow ?? true;

    // Apply preset style if not custom
    if (inputStyle && inputStyle !== 'custom') {
      wrapper.setAttribute('data-style', inputStyle);
    } else {
      // Remove preset style
      wrapper.removeAttribute('data-style');

      // Apply custom styles via CSS variables
      if (showInputBorder === false) {
        wrapper.style.setProperty('--ai360-input-border-width', '0');
      } else if (inputBorderWidth !== undefined) {
        wrapper.style.setProperty('--ai360-input-border-width', `${inputBorderWidth}px`);
      }

      if (inputBorderColor) {
        wrapper.style.setProperty('--ai360-input-border-color', inputBorderColor);
      }

      if (inputBorderRadius !== undefined) {
        wrapper.style.setProperty('--ai360-input-border-radius', `${inputBorderRadius}px`);
      }

      if (inputBackgroundColor) {
        wrapper.style.setProperty('--ai360-input-bg-color', inputBackgroundColor);
      }

      if (inputShadow) {
        wrapper.style.setProperty('--ai360-input-shadow-hover', '0 1px 6px rgba(32,33,36,0.28)');
        wrapper.style.setProperty('--ai360-input-shadow-focus', '0 1px 6px rgba(32,33,36,0.28)');
      } else {
        wrapper.style.setProperty('--ai360-input-shadow-hover', 'none');
        wrapper.style.setProperty('--ai360-input-shadow-focus', 'none');
      }
    }
  }, [config]);

  // Debounced search
  const debouncedSearch = useCallback(
    debounce((query: string) => {
      if (query.trim()) {
        onSearch(query);
      }
    }, debounceMs),
    [onSearch, debounceMs]
  );

  // Handle input change
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setValue(newValue);
    debouncedSearch(newValue);
  };

  // Handle submit
  const handleSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();
    if (value.trim()) {
      onSearch(value);
      setShowHistory(false);
    }
  };

  // Handle history item click
  const handleHistoryClick = (item: string) => {
    setValue(item);
    onSearch(item);
    setShowHistory(false);
  };

  // Handle clear
  const handleClear = () => {
    setValue('');
    inputRef.current?.focus();
  };

  // Handle key down
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSubmit();
    } else if (e.key === 'Escape') {
      setShowHistory(false);
    }
  };

  // Close history when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowHistory(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="ai360-search-box" ref={containerRef} data-voice-position={voiceButtonPosition}>
      <form onSubmit={handleSubmit} className="ai360-search-form">
        <div className="ai360-search-input-wrapper" ref={wrapperRef} data-voice-position={voiceButtonPosition}>
          <svg 
            className="ai360-search-icon" 
            viewBox="0 0 24 24"
            fill="currentColor"
          >
            <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
          </svg>
          
          <input
            ref={inputRef}
            type="text"
            value={value}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onFocus={() => setShowHistory(true)}
            placeholder={placeholder}
            autoFocus={autoFocus}
            className="ai360-search-input"
            autoComplete="off"
          />

          {value && (
            <button
              type="button"
              onClick={handleClear}
              className="ai360-search-clear"
              aria-label="Limpiar búsqueda"
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path 
                  d="M15 5L5 15M5 5l10 10" 
                  stroke="currentColor" 
                  strokeWidth="2" 
                  strokeLinecap="round"
                />
              </svg>
            </button>
          )}

          {/* Voice button inside input wrapper (right position only - always on the right) */}
          {(() => {
            const shouldShowInlineVoiceButton = showVoiceButton && onVoiceClick && voiceEnabled && 
              voiceButtonPosition === 'right';
            return shouldShowInlineVoiceButton && (
              <button
                type="button"
                onClick={onVoiceClick}
                className="ai360-search-voice"
                aria-label="Buscar por voz"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm-1 1v4h2v-4h-2zm-3.5-1.5C7.5 16.43 9.59 18 12 18s4.5-1.57 4.5-4.5H15c0 1.93-1.57 3.5-3.5 3.5S8 15.43 8 13.5H6.5z"/>
                </svg>
              </button>
            );
          })()}
        </div>

        {/* Voice button NOT shown below input - only floating bubble for 'bottom' position */}

        {showHistory && history.length > 0 && (
          <div className="ai360-search-history">
            <div className="ai360-search-history-header">
              <span className="ai360-search-history-title">Búsquedas recientes</span>
            </div>
            <ul className="ai360-search-history-list">
              {history.map((item, index) => (
                <li key={index} className="ai360-search-history-item">
                  <button
                    type="button"
                    onClick={() => handleHistoryClick(item)}
                    className="ai360-search-history-button"
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path 
                        d="M8 14A6 6 0 1 0 8 2a6 6 0 0 0 0 12z" 
                        stroke="currentColor" 
                        strokeWidth="1.5"
                      />
                      <path 
                        d="M8 5v3l2 2" 
                        stroke="currentColor" 
                        strokeWidth="1.5" 
                        strokeLinecap="round"
                      />
                    </svg>
                    <span>{item}</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </form>
    </div>
  );
};
